import { BellOutlined, CalendarOutlined } from "@ant-design/icons"
import { Badge, Button, Popover, Skeleton } from "antd"
import dayjs from "dayjs"
import React, { Suspense, lazy, useEffect, useRef, useState } from "react"
import { useTranslation } from "react-i18next"
import { type ConnectedProps, connect } from "react-redux"
import { useNavigate } from "react-router"

import { RoutesPaths } from "../../../constants/routes"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { fetchNotificationCounts } from "../../../redux/notificationCounts/notification-counts.thunks.ts"
import type { RootState } from "../../../store.ts"
import { showDesktopNotification } from "../../../utils/notification/browser-notification.ts"
import { showNotification } from "../../../utils/notification/show-notification"
import { calendarDeepLink } from "../../Calendar/helpers/calendar-deep-link.ts"
import { NOTIFICATION_PATHS } from "../api/services"
import { isNotificationClickable } from "../helpers/isNotificationClickable.ts"
import { markNotificationRead } from "../helpers/markNotificationRead.ts"
import { keepUnreadMembers } from "../helpers/keepUnreadMembers.ts"
import { parseNotificationMessage } from "../helpers/parseNotificationMessage.ts"
import {
  archiveReadNotifications,
  archiveNotifications,
  markAsRead,
  notificationArrived,
  setConnected,
  setHasNewNotification,
  setInitialNotifications,
  setUnreadCount
} from "../redux/notification.slice.ts"
import {
  NOTIFICATION_TYPE,
  type Notification,
  type NotificationCursor,
  WS_MESSAGE_TYPE
} from "../types/notification.types.ts"
import { NotificationsContent } from "./NotificationsContent.tsx"
import { NotificationsPermissionsAlert } from "./NotificationsPermissionsAlert.tsx"
import { NotificationsPopoverHeader } from "./NotificationsPopoverHeader.tsx"

const NotificationsModal = lazy(() => import("./NotificationsModal.tsx"))

interface NotificationsController extends ConnectedProps<typeof connector> {
  trigger?: (unreadCount: number) => React.ReactNode
}

function NotificationsController({
  notificationsInitial,
  unreadCount,
  hasNewNotification,
  accessToken,
  setConnected,
  setInitialNotifications,
  setHasNewNotification,
  notificationArrived,
  markAsRead,
  archiveNotifications,
  archiveReadNotifications,
  trigger,
  hasMoreInitial,
  nextInitial,
  setUnreadCount,
  isConnected,
  isMobile
}: NotificationsController) {
  const navigate = useNavigate()
  const wsRef = useRef<WebSocket | null>(null)
  const reset = useRef(true)

  // The bell no longer reads this slice. The fetch stays because nothing else
  // populates it and the sidebar's badges do read it.
  const dispatch = useAppDispatch()

  useEffect(() => {
    dispatch(fetchNotificationCounts())
  }, [dispatch])

  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  const [isModalOpen, setIsModalOpen] = useState(false)

  const [notifications, setNotifications] = useState<Notification[]>([])
  const [hasMore, setHasMore] = useState(true)
  const [next, setNext] = useState<NotificationCursor>(null)
  const [fetching, setIsFetching] = useState(false)
  const [permissionsNotification, setPermissionsNotification] =
    useState<Notification | null>(null)

  useEffect(() => {
    if (reset.current) {
      setNotifications(notificationsInitial)
      setHasMore(hasMoreInitial)
      setNext(nextInitial)
    } else {
      reset.current = true
    }
  }, [
    notificationsInitial,
    hasMoreInitial,
    nextInitial,
    isPopoverOpen,
    isModalOpen
  ])

  useEffect(() => {
    if (!accessToken) return

    // Heartbeat keeps the socket under the ALB's 60s idle timeout.
    const HEARTBEAT_MS = 25000
    const RECONNECT_BASE_MS = 1000
    const RECONNECT_MAX_MS = 30000
    let heartbeat: ReturnType<typeof setInterval> | undefined
    let reconnectTimer: ReturnType<typeof setTimeout> | undefined
    let reconnectAttempts = 0
    let closedByCleanup = false

    const connect = () => {
      const ws = new WebSocket(
        NOTIFICATION_PATHS.getNotifications(accessToken)
      )
      wsRef.current = ws

      ws.onopen = () => {
        setConnected(true)
        reconnectAttempts = 0
        heartbeat = setInterval(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(JSON.stringify({ type: WS_MESSAGE_TYPE.PING }))
          }
        }, HEARTBEAT_MS)
      }

      ws.onclose = () => {
        if (heartbeat) clearInterval(heartbeat)
        // Ignore a stale socket's close (a remount already replaced it).
        if (closedByCleanup || ws !== wsRef.current) return
        setConnected(false)
        const backoff = Math.min(
          RECONNECT_BASE_MS * 2 ** reconnectAttempts,
          RECONNECT_MAX_MS
        )
        reconnectAttempts += 1
        reconnectTimer = setTimeout(connect, backoff + Math.random() * 1000)
      }

      ws.onmessage = (event) => {
        const data = parseNotificationMessage(event.data)
        if (!data) return

        if (
          (data.type === WS_MESSAGE_TYPE.INITIAL_STATE ||
            data.type === WS_MESSAGE_TYPE.GET_ALL_MESSAGES) &&
          data.notifications
        ) {
          setInitialNotifications({
            notifications: data.notifications,
            unreadCount: data.unread_count || 0,
            hasMore: data.has_more || false,
            next: data.next_cursor
          })
          setIsFetching(false)
        }

        if (data.type === WS_MESSAGE_TYPE.NOTIFICATION_NEW && data.notification) {
          notificationArrived(data.notification)
        }

        // A reminder is time-sensitive — the badge alone (the default for
        // every other notification type) is too easy to miss, so this one
        // also gets an in-app toast and, permission allowing, a desktop
        // notification. Both reuse the same "Open event" action.
        //
        // Checked against `notification_name`, never `type`: `type` is the
        // broad category ("calendar") shared by every calendar notification
        // — an assignment, a reschedule, a cancellation ALL carry
        // `type: "calendar"` too, and `event_id` rides along on every one of
        // them (see calendars/notifications.py's `_notify`/`_deferred_notifier`
        // helpers). `notification_name` is the specific numeric code (111)
        // that actually distinguishes a reminder from the rest. Confirmed
        // live: comparing against `type` here made this block unreachable
        // against the real backend — it only "passed" in unit tests whose
        // mock fixture set the wrong field's value. `Number(...)` guards the
        // same numeric-vs-string wire-format risk either field could carry.
        //
        // `notification_name` itself is a NEW WS field (not yet deployed as
        // of this comment) — a backend predating it never sends the key at
        // all, so `reminder.verb === "reminder for"` (the literal string
        // `calendars/notifications.py`'s sweep uses for this exact
        // notification, and only this one) is the fallback until that
        // deploys. Drop the fallback once every environment has it.
        if (
          data.type === WS_MESSAGE_TYPE.NOTIFICATION_NEW &&
          data.notification?.data?.event_id
        ) {
          const reminder = data.notification

          const isReminder =
            reminder.notification_name != null
              ? Number(reminder.notification_name) ===
                NOTIFICATION_TYPE.CALENDAR_EVENT_REMINDER
              : reminder.verb === "reminder for"

          if (isReminder) {
            const eventId = reminder.data?.event_id
            // In-app SPA navigation, not window.open: EZBuild is already the
            // active tab whenever this toast (or the plain-Notification
            // click below) can fire — both paths only ever run while a tab
            // is open, unlike the closed-browser Web Push/service-worker
            // path, which genuinely may have no existing window to reuse and
            // is left untouched.
            const openEvent = () => {
              navigate(calendarDeepLink(undefined, eventId))
            }

            // `target.name` is the event's own title (see ezbuild_back's
            // `_serialize_generic_object`, which reads `obj.title` for a
            // CalendarEvent) — a clean signal that doesn't depend on parsing
            // the human-readable `description` sentence apart.
            const eventTitle =
              reminder.target?.name || reminder.description || reminder.verb
            const startAt = reminder.data?.start_at
            const endAt = reminder.data?.end_at
            const start = startAt ? dayjs(startAt).tz() : null
            // `end_at` is a NEW WS field, same deploy caveat as
            // notification_name above — absent on an older backend, in which
            // case the range degrades to a single start time rather than
            // showing nothing.
            const end = endAt ? dayjs(endAt).tz() : null
            const dayLabel = start
              ? start.isSame(dayjs().tz(), "day")
                ? "Today"
                : start.format("MMM D")
              : null
            const timeLabel = start
              ? end
                ? `${start.format("h:mm A")} – ${end.format("h:mm A")}`
                : start.format("h:mm A")
              : null
            const whenLabel =
              dayLabel && timeLabel ? `${dayLabel} · ${timeLabel}` : null

            // `.open()`, not `.info()`: the `info` TYPE carries antd's own
            // blue accent styling — this is a neutral card, not a system
            // alert.
            //
            // NEITHER `icon` NOR `message` NOR `btn` is used here: antd
            // reserves its own fixed indent/margin around each of those
            // slots (an icon slot indents `message`/`description` to make
            // room for it; `.ant-notification-notice-btn` carries a fixed
            // top margin of its own) — stacking those defaults is what
            // produced an oversized card with dead space between the time
            // and the action on every earlier pass, confirmed live twice.
            // Putting the ENTIRE card — icon, label, title, time, action —
            // into one self-owned flex layout inside `description` is what
            // actually makes every gap here a value this file controls,
            // rather than three different antd defaults stacking up.
            showNotification.open({
              // Required by antd's ArgsProps type, but deliberately empty:
              // the whole card (including its own "Upcoming event" label)
              // lives in `description` below, in one self-owned layout.
              message: null,
              description: (
                <div className="flex max-w-[380px] flex-col gap-2">
                  <div className="flex items-center gap-1.5">
                    <CalendarOutlined className="text-sm text-primary" />
                    <span className="text-xs font-medium uppercase tracking-wide text-gray-400">
                      Upcoming event
                    </span>
                  </div>

                  <div className="flex flex-col gap-1">
                    <span className="truncate text-lg font-semibold leading-tight text-gray-900">
                      {eventTitle}
                    </span>

                    <div className="flex items-center justify-between gap-3">
                      {whenLabel && (
                        <span className="truncate text-sm text-gray-500">
                          {whenLabel}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={openEvent}
                        className="shrink-0 text-sm font-medium text-primary hover:underline"
                      >
                        Open event →
                      </button>
                    </div>
                  </div>
                </div>
              ),
              duration: 0,
              style: { width: 400, padding: "16px 18px" }
            })

            showDesktopNotification({
              title: eventTitle,
              body: whenLabel || reminder.description || reminder.verb,
              onClick: openEvent
            })
          }
        }

        if (data.type === WS_MESSAGE_TYPE.NOTIFICATIONS_BATCH) {
          if (data.notifications) {
            setNotifications((prev) => [
              ...prev,
              ...(data.notifications as Notification[])
            ])
          }

          setHasMore(data.has_more || false)
          setNext(data.next_cursor)
        }

        // The server owns this number and every reply that knows it carries
        // it, so the field decides rather than a list of message types.
        if (typeof data.unread_count === "number") {
          setUnreadCount(data.unread_count)
        }

        if (data.type === WS_MESSAGE_TYPE.ERROR) {
          setIsFetching(false)
        }

        if (
          data.type === WS_MESSAGE_TYPE.NOTIFICATION_NEW &&
          data.notification?.type === NOTIFICATION_TYPE.PERMISSIONS &&
          data?.notification?.unread
        ) {
          setPermissionsNotification(data.notification)
        }
      }
    }

    connect()

    return () => {
      closedByCleanup = true
      if (heartbeat) clearInterval(heartbeat)
      if (reconnectTimer) clearTimeout(reconnectTimer)
      wsRef.current?.close()
      // Reset here too: onclose returns early when closedByCleanup is set.
      setConnected(false)
    }
  }, [
    accessToken,
    navigate,
    setConnected,
    setInitialNotifications,
    setHasNewNotification,
    notificationArrived,
    setUnreadCount
  ])

  const { t } = useTranslation()

  const sendWhenOpen = (payload: Record<string, unknown>): boolean => {
    if (wsRef.current?.readyState !== WebSocket.OPEN) {
      showNotification.warning({
        message: t("notifications.bell.actionUnavailable")
      })
      return false
    }

    wsRef.current.send(JSON.stringify(payload))
    return true
  }

  // A row stands for every update behind it, and the server takes one id at a
  // time, so what is sent has to reach exactly what the local update changes.
  const idsFor = (target: number | number[]): number[] => {
    const ids = new Set(Array.isArray(target) ? target : [target])
    for (const row of notifications) {
      if (!ids.has(row?.id)) continue
      for (const member of row?.group_members ?? []) ids.add(member?.id)
    }
    return [...ids]
  }

  const handleMarkAsRead = (target: number | number[] | null) => {
    reset.current = false

    if (target === null) {
      if (
        !sendWhenOpen({
          type: WS_MESSAGE_TYPE.MARK_READ,
          notification_id: null
        })
      ) {
        reset.current = true
        return
      }

      markAsRead(null)
      setNotifications((prev) => prev.map(markNotificationRead))
      return
    }

    const ids = idsFor(target)
    const sent = ids.every((id) =>
      sendWhenOpen({ type: WS_MESSAGE_TYPE.MARK_READ, notification_id: id })
    )
    if (!sent) {
      reset.current = true
      return
    }

    const read = new Set(ids)
    markAsRead(ids)
    setNotifications((prev) =>
      prev.map((n) => (read.has(n.id) ? markNotificationRead(n) : n))
    )
  }

  const onLoadMore = () => {
    sendWhenOpen({ type: WS_MESSAGE_TYPE.FETCH_MORE, after_id: next })
  }

  const handleFetchNotifications = () => {
    setIsFetching(true)
    if (!sendWhenOpen({ type: WS_MESSAGE_TYPE.GET_ALL_MESSAGES })) {
      setIsFetching(false)
      return
    }
    setHasNewNotification(false)
  }

  const handleArchive = (target: number | number[] | null) => {
    reset.current = false

    // A null target means "everything read", not "everything".
    if (target === null) {
      if (!sendWhenOpen({ type: WS_MESSAGE_TYPE.ARCHIVE_ALL_READ })) {
        reset.current = true
        return
      }

      archiveReadNotifications()
      setNotifications((prev) =>
        prev
          .map(keepUnreadMembers)
          .filter((n): n is Notification => n !== null)
      )
      return
    }

    const ids = idsFor(target)
    const sent = ids.every((id) =>
      sendWhenOpen({ type: WS_MESSAGE_TYPE.ARCHIVE, id })
    )
    if (!sent) {
      reset.current = true
      return
    }

    const archived = new Set(ids)
    archiveNotifications(ids)
    setNotifications((prev) => prev.filter((n) => !archived.has(n.id)))
  }

  const handleCardClick = (notification: Notification) => {
    if (!isNotificationClickable(notification)) return

    const target = notification?.target

    switch (target?.type) {
      case "project":
        window.open(RoutesPaths.project.view(target.id).overview, "_blank")
        break
      case "lead":
        window.open(RoutesPaths.lead.view(target.id), "_blank")
        break
      case "calendarevent":
        window.open(
          calendarDeepLink(undefined, notification.data?.event_id || target.id),
          "_blank"
        )
        break
    }
  }

  const openModal = () => {
    setIsPopoverOpen(false)
    setIsModalOpen(true)
  }

  const bell = trigger ? (
    trigger(unreadCount)
  ) : (
    <Badge
      count={unreadCount}
      offset={unreadCount > 9 ? [-8, 2] : [-4, 2]}
      className="z-10"
    >
      <Button
        type="text"
        icon={<BellOutlined />}
        className="shadow-sm border border-gray-100"
        size="large"
      />
    </Badge>
  )

  return (
    <>
      {permissionsNotification && (
        <Suspense>
          <NotificationsPermissionsAlert
            permissionsNotification={permissionsNotification}
            onMarkAsRead={handleMarkAsRead}
            onPermissionsNotification={() => setPermissionsNotification(null)}
          />
        </Suspense>
      )}
      {isMobile ? (
        // The popover is right-aligned to a mid-header trigger, so it cannot fit a phone.
        <span className="inline-flex" onClick={() => setIsModalOpen(true)}>
          {bell}
        </span>
      ) : (
        <Popover
          trigger="click"
          open={isPopoverOpen}
          onOpenChange={(value) => setIsPopoverOpen(value)}
          placement="bottomRight"
          title={
            <NotificationsPopoverHeader
              unreadCount={unreadCount}
              onMarkAllAsRead={() => handleMarkAsRead(null)}
              onExpand={openModal}
              disabled={!isConnected}
            />
          }
          content={
            <NotificationsContent
              notifications={notifications}
              hasNewNotification={hasNewNotification}
              onMarkAsRead={handleMarkAsRead}
              onCardClick={handleCardClick}
              onArchive={handleArchive}
              hasMore={hasMore}
              onLoadMore={onLoadMore}
              refetchNotifications={handleFetchNotifications}
              fetching={fetching}
            />
          }
        >
          {bell}
        </Popover>
      )}

      {isModalOpen && (
        <Suspense
          fallback={
            <Skeleton.Node
              active
              className={"fixed inset-0 z-10  m-auto h-[90vh] w-[90vw]"}
            />
          }
        >
          <NotificationsModal
            open={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            notifications={notifications}
            unreadCount={unreadCount}
            onMarkAsRead={handleMarkAsRead}
            onCardClick={handleCardClick}
            onArchive={handleArchive}
            hasMore={hasMore}
            onLoadMore={onLoadMore}
            refetchNotifications={handleFetchNotifications}
            hasNewNotification={hasNewNotification}
            fetching={fetching}
            disabled={!isConnected}
            isMobile={isMobile}
          />
        </Suspense>
      )}
    </>
  )
}

const mapStateToProps = (state: RootState) => ({
  accessToken: state.auth.accessToken,
  notificationsInitial: state.notification.notifications,
  unreadCount: state.notification.unreadCount,
  hasNewNotification: state.notification.hasNewNotification,
  hasMoreInitial: state.notification.hasMore,
  nextInitial: state.notification.next,
  isConnected: state.notification.isConnected,
  isMobile: state.nav.isMobile
})

const mapDispatchToProps = {
  markAsRead,
  setConnected,
  setHasNewNotification,
  notificationArrived,
  setInitialNotifications,
  archiveNotifications,
  archiveReadNotifications,
  setUnreadCount
}

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(NotificationsController)
